import { useEffect, useRef } from 'react';

import type { FaseDaVoz } from './useConversaPorVoz';

/**
 * A onda de barras embaixo do orbe (mockup do usuário, 13/09/2026): ouvindo, segue o microfone;
 * falando, segue o áudio real da VecchIA. Barras espelhadas a partir do centro, com as faixas de
 * frequência da fala (graves no meio, agudos nas pontas). Sem espectro (voz do navegador), uma
 * ondulação suave de fala. Desenha num canvas, sem re-render.
 */

const BARRAS = 28;
const LARGURA_DA_BARRA = 3;
const INTERVALO = 3;
const ALTURA = 36;
/** Faixa do espectro que vira barra: ~90 Hz a ~5 kHz, em escala logarítmica. */
const HZ_MIN = 90;
const HZ_MAX = 5000;

export interface OndaDaVozProps {
    fase: FaseDaVoz;
    /** O espectro atual (0 a 255 por posição) e o tamanho de cada posição em Hz; nulo sem analisador. */
    lerEspectro: () => { valores: ArrayLike<number>; hzPorPosicao: number } | null;
    rotulo: string;
    /** Nível de 0 a 1, para o `meter` acessível. */
    nivel: number;
    medidor: boolean;
}

export function OndaDaVoz({ fase, lerEspectro, rotulo, nivel, medidor }: OndaDaVozProps) {
    const canvas = useRef<HTMLCanvasElement>(null);
    const faseRef = useRef(fase);
    faseRef.current = fase;
    const leitor = useRef(lerEspectro);
    leitor.current = lerEspectro;

    useEffect(() => {
        const alvo = canvas.current;
        const ctx = alvo?.getContext('2d');
        if (!alvo || !ctx) return undefined;
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        const largura = BARRAS * LARGURA_DA_BARRA + (BARRAS - 1) * INTERVALO;
        alvo.width = Math.round(largura * dpr);
        alvo.height = Math.round(ALTURA * dpr);
        ctx.scale(dpr, dpr);
        const reduzido = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
        const alturas = new Float32Array(BARRAS);
        const metade = Math.ceil(BARRAS / 2);
        let quadro = 0;
        let ultimo = performance.now();

        const gradiente = ctx.createLinearGradient(0, 0, largura, 0);
        gradiente.addColorStop(0, 'rgba(37, 248, 172, 0.15)');
        gradiente.addColorStop(0.5, 'rgba(7, 110, 86, 0.95)');
        gradiente.addColorStop(1, 'rgba(37, 248, 172, 0.15)');

        const desenhar = (agora: number) => {
            quadro = requestAnimationFrame(desenhar);
            if (document.hidden) return;
            const dt = Math.min(64, agora - ultimo);
            ultimo = agora;
            const faseAtual = faseRef.current;
            const ativa = faseAtual === 'listening' || faseAtual === 'speaking';
            const espectro = ativa ? leitor.current() : null;
            const tempo = agora / 1000;

            for (let i = 0; i < metade; i += 1) {
                let alvoDaBarra = 0.08;
                if (espectro) {
                    const hz = HZ_MIN * (HZ_MAX / HZ_MIN) ** (i / (metade - 1));
                    const posicao = Math.round(hz / espectro.hzPorPosicao);
                    const valor = (espectro.valores[posicao] ?? 0) / 255;
                    alvoDaBarra = Math.max(0.08, Math.min(1, valor ** 1.4 * 1.35));
                } else if (faseAtual === 'speaking') {
                    alvoDaBarra = 0.25 + 0.35 * Math.abs(Math.sin(tempo * 5.2 + i * 0.7)) * (0.6 + 0.4 * Math.sin(tempo * 1.7));
                }
                /* Sobe rápido, desce devagar: a onda respira em vez de piscar. */
                const k = alvoDaBarra > (alturas[i] ?? 0) ? 1 - Math.exp(-dt / (reduzido ? 180 : 60)) : 1 - Math.exp(-dt / (reduzido ? 500 : 240));
                alturas[i] = (alturas[i] ?? 0) + (alvoDaBarra - (alturas[i] ?? 0)) * k;
            }

            ctx.clearRect(0, 0, largura, ALTURA);
            ctx.fillStyle = gradiente;
            for (let i = 0; i < BARRAS; i += 1) {
                const distancia = Math.abs(i - (BARRAS - 1) / 2);
                const indice = Math.min(metade - 1, Math.floor(distancia));
                const altura = Math.max(3, (alturas[indice] ?? 0) * ALTURA);
                const x = i * (LARGURA_DA_BARRA + INTERVALO);
                const y = (ALTURA - altura) / 2;
                ctx.beginPath();
                ctx.roundRect(x, y, LARGURA_DA_BARRA, altura, LARGURA_DA_BARRA / 2);
                ctx.fill();
            }
        };
        quadro = requestAnimationFrame(desenhar);
        return () => cancelAnimationFrame(quadro);
    }, []);

    const porcentagem = Math.round(nivel * 100);
    return (
        <span
            className="brain-vozmodo__onda"
            {...(medidor
                ? { role: 'meter', 'aria-label': 'Nível do microfone', 'aria-valuemin': 0, 'aria-valuemax': 100, 'aria-valuenow': porcentagem }
                : { 'aria-hidden': true })}
            title={rotulo}
        >
            <canvas ref={canvas} />
        </span>
    );
}
